import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import AppAuth from '../services/AppAuth';

export const AuthContext = createContext();

// Map Dailey Core roles to permissions (mirrors server-side coreRolePermissionMap)
const coreRolePermissionMap = {
  'core.admin': {
    can_manage_forms: true,
    can_view_all_locations: true,
    can_manage_users: true,
    can_view_reports: true,
    can_view_audit_logs: true
  },
  'forms.admin': {
    can_manage_forms: true,
    can_view_all_locations: true,
    can_manage_users: true,
    can_view_reports: true,
    can_view_audit_logs: true
  },
  'forms.manager': {
    can_manage_forms: true,
    can_view_all_locations: false,
    can_manage_users: false,
    can_view_reports: true,
    can_view_audit_logs: false
  },
  'forms.user': {
    can_manage_forms: false,
    can_view_all_locations: false,
    can_manage_users: false,
    can_view_reports: false,
    can_view_audit_logs: false
  }
};

export const AuthProvider = ({ children }) => {
  const [auth] = useState(() => new AppAuth());
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return !!token;
  });
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [roles, setRoles] = useState(() => {
    const savedRoles = localStorage.getItem('roles');
    return savedRoles ? JSON.parse(savedRoles) : [];
  });
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(() => {
    const savedBuilding = localStorage.getItem('selectedBuilding');
    return savedBuilding ? JSON.parse(savedBuilding) : null;
  });
  const [username, setUsername] = useState(() => {
    return localStorage.getItem('username') || '';
  });

  const logout = useCallback(async () => {
    await auth.logout();
    localStorage.removeItem('selectedBuilding');
    localStorage.removeItem('user');
    localStorage.removeItem('username');
    localStorage.removeItem('roles');
    setUser(null);
    setRoles([]);
    setIsAuthenticated(false);
    setUsername('');
    setSelectedBuilding(null);
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?session=expired';
    }
  }, [auth]);

  const initializeAuth = useCallback(async () => {
    if (window.location.pathname.includes('/login')) {
      setLoading(false);
      return;
    }

    const data = await auth.validateToken();
    if (data && data.valid) {
      setIsAuthenticated(true);
      setUser(data.user || null);
      setRoles(data.roles || []);
      const displayName = data.user?.email || data.user?.username || username;
      if (displayName) {
        setUsername(displayName);
      }
    } else {
      // Only logout if tokens were actually cleared (true auth failure)
      // If tokens still exist, it was likely a temporary error
      const hasTokens = localStorage.getItem('access_token') || localStorage.getItem('token');
      if (!hasTokens) {
        // Tokens were cleared - this was a real auth failure
        setIsAuthenticated(false);
        setUser(null);
        setRoles([]);
      }
      // If tokens still exist, keep current auth state and try again later
    }
    setLoading(false);
  }, [auth, username]);

  useEffect(() => {
    initializeAuth();
    const interval = setInterval(initializeAuth, 60000);
    return () => clearInterval(interval);
  }, [initializeAuth]);

  useEffect(() => {
    if (selectedBuilding) {
      localStorage.setItem('selectedBuilding', JSON.stringify(selectedBuilding));
    }
  }, [selectedBuilding]);

  useEffect(() => {
    if (username) {
      localStorage.setItem('username', username);
    } else {
      localStorage.removeItem('username');
    }
  }, [username]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  useEffect(() => {
    if (roles && roles.length > 0) {
      localStorage.setItem('roles', JSON.stringify(roles));
    } else {
      localStorage.removeItem('roles');
    }
  }, [roles]);

  // Compute userRole permissions from roles array
  const userRole = useMemo(() => {
    if (!roles || roles.length === 0) {
      return null;
    }

    // Merge permissions from all assigned roles (highest permission wins)
    const permissions = {
      can_manage_forms: false,
      can_view_all_locations: false,
      can_manage_users: false,
      can_view_reports: false,
      can_view_audit_logs: false
    };

    for (const role of roles) {
      const rolePerms = coreRolePermissionMap[role];
      if (rolePerms) {
        Object.keys(permissions).forEach(key => {
          if (rolePerms[key]) {
            permissions[key] = true;
          }
        });
      }
    }

    return permissions;
  }, [roles]);

  const login = useCallback(
    async (identifier, password) => {
      const result = await auth.login(identifier, password);
      setIsAuthenticated(true);
      setUser(result.user || null);
      setRoles(result.roles || []);
      const displayName = result.user?.email || result.user?.username || identifier;
      if (displayName) {
        setUsername(displayName);
      }
      return result;
    },
    [auth]
  );

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      loading,
      setIsAuthenticated,
      selectedBuilding,
      setSelectedBuilding,
      username,
      setUsername,
      logout,
      login,
      user,
      roles,
      userRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};
